import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Filter, Eye, Shield, ArrowRight, Plus, Minus } from "lucide-react";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  });
};

const isRoleAction = (action) => (action || "").startsWith("ROLE_");

export default function AuditTab({
  auditLogs,
  auditFilters,
  auditPagination,
  onFilterChange,
  onApplyFilters,
  onExport,
  onPaginationChange
}) {
  const [detailLog, setDetailLog] = useState(null);

  const applyRoleFilter = () => {
    onFilterChange("action_category", "role_management");
    onApplyFilters();
  };

  return (
    <div className="space-y-4" data-testid="audit-tab-content">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Audit Logs</h2>
        <Button 
          onClick={onExport} 
          variant="outline" 
          className="border-gray-200 text-gray-700 hover:bg-gray-50"
          data-testid="export-audit-logs-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>
      
      {/* Filters */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Input
              placeholder="Filter by category..."
              value={auditFilters.action_category}
              onChange={(e) => onFilterChange("action_category", e.target.value)}
              className="bg-gray-50 border-gray-200"
              data-testid="filter-category"
            />
            <select
              value={auditFilters.actor_type || ""}
              onChange={(e) => onFilterChange("actor_type", e.target.value)}
              className="h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm"
              data-testid="filter-actor-type"
            >
              <option value="">All actors</option>
              <option value="staff">Staff / console</option>
              <option value="end_user">End users</option>
            </select>
            <Input
              type="date"
              placeholder="From date"
              value={auditFilters.date_from}
              onChange={(e) => onFilterChange("date_from", e.target.value)}
              className="bg-gray-50 border-gray-200"
              data-testid="filter-date-from"
            />
            <Input
              type="date"
              placeholder="To date"
              value={auditFilters.date_to}
              onChange={(e) => onFilterChange("date_to", e.target.value)}
              className="bg-gray-50 border-gray-200"
              data-testid="filter-date-to"
            />
            <div className="flex gap-2">
              <Button 
                onClick={onApplyFilters} 
                className="bg-red-500 hover:bg-red-600 text-white flex-1"
                data-testid="apply-filters-btn"
              >
                <Filter className="h-4 w-4 mr-2" />
                Apply
              </Button>
              <Button
                onClick={applyRoleFilter}
                variant="outline"
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                title="Show only role & permission changes"
                data-testid="role-filter-btn"
              >
                <Shield className="h-4 w-4 mr-1" />
                Role changes
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Logs Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left p-4 text-gray-600 font-medium">Timestamp</th>
                <th className="text-left p-4 text-gray-600 font-medium">Admin</th>
                <th className="text-left p-4 text-gray-600 font-medium">Action</th>
                <th className="text-left p-4 text-gray-600 font-medium">Target</th>
                <th className="text-left p-4 text-gray-600 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log) => (
                <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4 text-sm text-gray-500">
                    {formatDate(log.timestamp)}
                  </td>
                  <td className="p-4">
                    <p className="text-sm text-gray-900">{log.actor_name || log.admin_email}</p>
                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className={log.actor_type === "end_user"
                          ? "border-sky-300 bg-sky-50 text-sky-700 text-[10px] px-1.5 py-0"
                          : "border-violet-300 bg-violet-50 text-violet-700 text-[10px] px-1.5 py-0"}
                        data-testid={`actor-type-${log.id}`}
                      >
                        {log.actor_type === "end_user" ? "End user" : "Staff"}
                      </Badge>
                      <span className="text-xs text-gray-500">{log.admin_role}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <Badge
                      variant="outline"
                      className={isRoleAction(log.action)
                        ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                        : "border-gray-300 text-gray-700"}
                    >
                      {isRoleAction(log.action) && <Shield className="h-3 w-3 mr-1" />}
                      {log.action}
                    </Badge>
                  </td>
                  <td className="p-4 text-sm text-gray-600">
                    {log.target_type && `${log.target_type}: ${log.target_name || log.target_id}`}
                  </td>
                  <td className="p-4">
                    {log.changes && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                        onClick={() => setDetailLog(log)}
                        data-testid={`view-changes-${log.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        {isRoleAction(log.action) ? "Before / after" : "View"}
                      </Button>
                    )}
                    {!log.changes && log.event && (
                      <span className="text-xs text-gray-600" data-testid={`event-detail-${log.id}`}>
                        {Object.entries(log.event).filter(([, v]) => v !== null && v !== undefined)
                          .map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </span>
                    )}
                    {log.involves_pii && (
                      <Badge className="ml-2 bg-amber-100 text-amber-700 border border-amber-300">
                        PII
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
              {auditLogs.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-500">
                    No audit logs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      
      {/* Pagination */}
      <div className="flex justify-between items-center text-sm text-gray-500">
        <p>Showing {auditLogs.length} of {auditPagination.total} logs</p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={auditPagination.skip === 0}
            onClick={() => onPaginationChange(auditPagination.skip - auditPagination.limit)}
            className="border-gray-200"
            data-testid="pagination-prev"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={auditPagination.skip + auditPagination.limit >= auditPagination.total}
            onClick={() => onPaginationChange(auditPagination.skip + auditPagination.limit)}
            className="border-gray-200"
            data-testid="pagination-next"
          >
            Next
          </Button>
        </div>
      </div>

      {detailLog && (
        <ChangesModal log={detailLog} onClose={() => setDetailLog(null)} />
      )}
    </div>
  );
}

function ChangesModal({ log, onClose }) {
  const changes = log.changes || {};
  const { before, after } = changes;
  const beforeIsArr = Array.isArray(before);
  const afterIsArr = Array.isArray(after);

  let body;
  if (beforeIsArr || afterIsArr) {
    // Permission-list diff (role permission edits / create)
    const b = new Set(beforeIsArr ? before : []);
    const a = new Set(afterIsArr ? after : []);
    const added = [...a].filter((x) => !b.has(x)).sort();
    const removed = [...b].filter((x) => !a.has(x)).sort();
    const kept = [...a].filter((x) => b.has(x)).sort();
    body = (
      <div className="space-y-4" data-testid="changes-permission-diff">
        <div className="flex gap-3 text-sm">
          <span className="text-emerald-700 font-medium">+{added.length} added</span>
          <span className="text-red-700 font-medium">-{removed.length} removed</span>
          <span className="text-slate-500">{kept.length} unchanged</span>
        </div>
        {added.length > 0 && (
          <PermGroup title="Granted" color="emerald" icon={Plus} items={added} />
        )}
        {removed.length > 0 && (
          <PermGroup title="Revoked" color="red" icon={Minus} items={removed} />
        )}
        {added.length === 0 && removed.length === 0 && (
          <p className="text-sm text-slate-500">No effective permission change (saved identical set).</p>
        )}
        {kept.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-slate-600">Unchanged permissions ({kept.length})</summary>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {kept.map((p) => (
                <span key={p} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">{p}</span>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  } else if ((before && typeof before === "object") || (after && typeof after === "object")) {
    // Field-level before -> after (admin edits, role assignment, etc.)
    const keys = [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])];
    body = (
      <div className="space-y-2" data-testid="changes-field-diff">
        {keys.map((k) => (
          <div key={k} className="flex items-center gap-2 text-sm border rounded-md p-2">
            <span className="font-medium text-slate-700 w-28 shrink-0">{k}</span>
            <span className="text-red-600 line-through break-all">{String((before || {})[k] ?? "—")}</span>
            <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="text-emerald-700 break-all">{String((after || {})[k] ?? "—")}</span>
          </div>
        ))}
      </div>
    );
  } else {
    body = (
      <pre className="text-xs bg-slate-50 border rounded-md p-3 overflow-auto max-h-[50vh]">
        {JSON.stringify(changes, null, 2)}
      </pre>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-changes-modal">
        <DialogTitle className="flex items-center gap-2">
          {isRoleAction(log.action) && <Shield className="h-5 w-5 text-indigo-600" />}
          {log.action.replaceAll("_", " ")}
        </DialogTitle>
        <DialogDescription>
          {log.target_type ? `${log.target_type}: ${log.target_name || log.target_id} · ` : ""}
          by {log.admin_email} · {formatDate(log.timestamp)}
        </DialogDescription>
        {log.reason && (
          <p className="text-sm text-slate-600"><span className="font-medium">Reason:</span> {log.reason}</p>
        )}
        {body}
      </DialogContent>
    </Dialog>
  );
}

function PermGroup({ title, color, icon: Icon, items }) {
  const styles = color === "emerald"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-red-50 text-red-700 border-red-200";
  return (
    <div>
      <p className={`text-xs font-semibold flex items-center gap-1 mb-1.5 ${color === "emerald" ? "text-emerald-700" : "text-red-700"}`}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((p) => (
          <span key={p} className={`px-2 py-0.5 rounded border text-xs ${styles}`}>{p}</span>
        ))}
      </div>
    </div>
  );
}
